<template>
  <!-- 舰礼展示 · 3D Cover Flow：中间放大高亮，两侧往后退并内转，形成纵深感 -->
  <div class="gift-arc" :style="rootStyle">
    <!-- 顶光：颜色跟着后台主题色走，右下角的「亮度 / 灰度」滑条实时调它 -->
    <div class="beam-layer">
      <span class="beam-top"></span>
      <span class="beam-glow"></span>
      <span class="beam-floor"></span>
    </div>

    <div class="arc-stage" ref="stageEl" @mousedown="onDown" @touchstart.passive="onTouchStart"
      @touchmove.prevent="onTouchMove" @touchend="onTouchEnd" :style="{ height: stageH + 'px' }">
      <div class="arc-inner" ref="arcInnerEl">
        <div v-for="(it, i) in items" :key="i" class="arc-card"
          :class="{ active: i === active, side: i !== active }" :style="cardStyle(i)" @click.stop="pick(i)">
          <div class="card-pic" :style="{ height: picH + 'px' }">
            <!-- 中间那张点一下能放大看原图；两边的点一下就挪到中间 -->
            <el-image v-if="i === active" :src="it.src" fit="contain" :preview-src-list="sources"
              :initial-index="i" :preview-teleported="true" class="arc-img" @load="onImgLoad" />
            <img v-else :src="it.src" :alt="it.title || ('舰礼 ' + (i + 1))" draggable="false" class="arc-img" @load="onImgLoad" />
          </div>
          <span class="card-halo" v-if="i === active"></span>
        </div>
      </div>
    </div>

    <!-- 中间那排入口（往期舰礼 / 填写信息） -->
    <div class="arc-center">
      <slot name="center" :current="currentIndex"></slot>
    </div>

    <div class="arc-dots" v-if="items.length > 1">
      <span v-for="(it, i) in items" :key="i" class="dot" :class="{ on: i === active }" @click="pick(i)"></span>
    </div>
  </div>
</template>

<script setup>
/* 舰礼展示轮播（按主人给的 PRD 做的 3D Cover Flow）
 * - 中间一件放大高亮，两侧往后退 + 内转 + 降透明度，配合容器的 perspective 出纵深感
 * - 配色不写死：主色/背景色/文字色/顶光色全部走 CSS 变量（页面那边用后台设置注入）
 * - 顶光是固定的；没人操作时每 interval（默认 3 秒）自动翻下一张，循环播
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps({
  // [{ src, title, date }]
  items: { type: Array, default: () => [] },
  theme: { type: String, default: '#409eff' },
  startIndex: { type: Number, default: -1 },
  interval: { type: Number, default: 3000 }        // 自动轮播间隔（毫秒），0 = 不自动播
});

const SIDE_SCALE = 0.72;      // 两侧卡片缩小到多少
const SIDE_ROT = 38;          // 两侧向内旋转的角度（度）

/* ---------------- 尺寸：跟着容器宽度自己算（手机端适配）----------------
 * 原来 SIDE_X 写死 300 / 卡片写死 360，手机上容器只有 320 宽，
 * 中间那张卡就比容器宽，两侧的卡更是整张跑到屏幕外。
 * 现在卡片宽度 = 容器宽 - 16（最多 460），侧向错开按卡片宽度比例走，
 * 窄屏自动收窄，位置由 JS 的 transform 说了算（CSS 里手机端不再写死宽度）。 */
const CARD_W_MAX = 460;
const CARD_MIN_H = 140;
const CARD_MAX_H = 460;
/* 窄/矮屏再收一档：图片太高会把「按钮 / 提示 / 备案号」挤出屏幕
   （舰长礼物页桌面端是锁滚动的，看不见就是真看不见） */
const isCompact = () => window.innerWidth <= 1024 || window.innerHeight <= 900;
const cardW = ref(360);
const SIDE_X = computed(() => Math.round(cardW.value * 0.62));   // 两侧错开的横向距离
// 舞台高度：宽屏 430；窄屏收紧到卡片高度 + 余量，别白白占一屏
const stageH = computed(() => {
  if (cardW.value < 300) return Math.max(240, Math.round(300 + (cardW.value - 220) * 0.35));
  return window.innerWidth <= 1024 || window.innerHeight <= 900 ? 348 : 430;
});

const mid = Math.floor((Math.max(0, props.items.length - 1)) / 2);
const active = ref(props.startIndex >= 0 ? props.startIndex : mid);
const currentIndex = computed(() => active.value);
const sources = computed(() => props.items.map((i) => i.src));

/* 顶光是固定的（主人不要那个微调面板了） */
const rootStyle = computed(() => ({ '--theme-primary-color': props.theme || '#409eff' }));

/** 每张卡的位移 / 旋转 / 缩放 / 透明度 */
const cardStyle = (i) => {
  const d = i - active.value;
  const abs = Math.abs(d);
  if (abs > 3) return { transform: 'translate(-50%, -50%) scale(.5)', opacity: '0', zIndex: '0', pointerEvents: 'none' };
  const x = d * SIDE_X.value;
  const rotY = d === 0 ? 0 : (d > 0 ? -SIDE_ROT : SIDE_ROT);   // 往中间转，像排成一段弧
  const sc = d === 0 ? 1 : SIDE_SCALE;
  const op = d === 0 ? 1 : Math.max(0.16, 0.42 - (abs - 1) * 0.14);
  return {
    width: cardW.value + 'px',
    transform: 'translate(-50%, -50%) translateX(' + x + 'px) rotateY(' + rotY + 'deg) scale(' + sc + ')',
    zIndex: String(20 - abs),
    opacity: String(op),
    filter: d === 0 ? 'none' : 'brightness(.55) saturate(.85)'
  };
};

/* 图区高度跟着图片比例走（宽图不会缩成一条），宽度按当前卡片宽度算 */
const picH = ref(320);
let picRatio = 320 / 360;
const applyPicH = () => {
  picH.value = Math.max(CARD_MIN_H, Math.min(CARD_MAX_H, Math.round(cardW.value * picRatio)));
};
const onImgLoad = (e) => {
  const im = e && e.target;
  if (!im || !im.naturalWidth || !im.naturalHeight) return;
  picRatio = im.naturalHeight / im.naturalWidth;
  applyPicH();
};

/* 量容器宽度：决定卡片宽度和两侧错开距离（窄屏自动收） */
const stageEl = ref(null);
const arcInnerEl = ref(null);
let ro = 0;
const measure = () => {
  const el = arcInnerEl.value || stageEl.value;
  const w = el ? el.clientWidth : 0;
  if (!w) return;
  const cap = isCompact() ? Math.min(CARD_W_MAX, 384) : CARD_W_MAX;
  cardW.value = Math.min(cap, Math.max(160, w - 16));
  applyPicH();
};

/* ---------------- 自动轮播 ----------------
 * 主人要求：没人操作的时候每 5 秒自动翻到下一张，循环播。
 * 手动切（拖拽 / 点小圆点）之后重新计时，别刚点完就被自动跳走。 */
let timer = 0;
const stopAuto = () => { if (timer) { window.clearInterval(timer); timer = 0; } };
const startAuto = () => {
  stopAuto();
  if (!props.interval || props.items.length < 2) return;
  timer = window.setInterval(() => {
    const n = props.items.length;
    if (n > 1) active.value = (active.value + 1) % n;
  }, props.interval);
};
onMounted(() => {
  measure();
  startAuto();
  requestAnimationFrame(measure);            // 首帧布局完再量一次，避免量到 0
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(measure);
    if (arcInnerEl.value) ro.observe(arcInnerEl.value);
    else if (stageEl.value) ro.observe(stageEl.value);
  } else {
    window.addEventListener('resize', measure);
  }
});
onBeforeUnmount(() => {
  stopAuto();
  if (ro) { ro.disconnect(); ro = 0; }
  window.removeEventListener('resize', measure);
});

const pick = (i) => {
  if (i >= 0 && i < props.items.length) active.value = i;
  startAuto();                       // 手动选过 → 重新计时
};

/** dir：+1 下一张 / -1 上一张；byAuto=true 表示这次是自动播的，不要重置计时 */
const step = (dir, byAuto) => {
  const n = props.items.length;
  if (!n) return;
  active.value = (active.value + dir + n) % n;
  if (!byAuto) startAuto();
};

/* 拖动 / 滑动 */
let dragging = false;
let startX = 0;
let moved = false;
const onDown = (e) => { dragging = true; moved = false; startX = e.clientX; };
const onTouchStart = (e) => { const t = e.touches && e.touches[0]; if (t) { dragging = true; moved = false; startX = t.clientX; } };
const handleMove = (x) => {
  if (!dragging) return;
  const dx = x - startX;
  if (!moved && Math.abs(dx) > 8) moved = true;
  if (moved && Math.abs(dx) > 70) {
    step(dx < 0 ? 1 : -1);           // 手动拖 → step 里会重新计时
    startX = x;
  }
};
const onTouchMove = (e) => { const t = e.touches && e.touches[0]; if (t) handleMove(t.clientX); };
const onTouchEnd = () => { dragging = false; };

if (typeof window !== 'undefined') {
  window.addEventListener('mousemove', (e) => handleMove(e.clientX));
  window.addEventListener('mouseup', () => { window.setTimeout(() => { dragging = false; }, 0); });
}
</script>

<style scoped>
.gift-arc {
  position: relative;
  width: 100%;
  padding: 8px 0 4px;
  user-select: none;
  overflow: hidden;
}

/* ---------- 顶光（颜色 = 主题色的低透明度版本，亮度/灰度可调） ---------- */
.beam-layer { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.beam-top {
  position: absolute; left: 50%; top: -22%; width: 62%; height: 78%; transform: translateX(-50%);
  background: radial-gradient(closest-side, var(--theme-primary-color), transparent 72%);
  opacity: calc(.30 * var(--beam-brightness));
  filter: blur(18px) grayscale(var(--beam-gray));
  animation: beam-breathe 6s ease-in-out infinite;
}
.beam-glow {
  position: absolute; left: 50%; top: 8%; width: 84%; height: 70%; transform: translateX(-50%);
  background: radial-gradient(closest-side, rgba(255, 255, 255, .16), transparent 70%);
  opacity: var(--beam-brightness);
  filter: blur(24px) grayscale(var(--beam-gray));
}
.beam-floor {
  position: absolute; left: 50%; bottom: 2%; width: 72%; height: 26%; transform: translateX(-50%);
  background: radial-gradient(closest-side, var(--theme-primary-color), transparent 72%);
  opacity: calc(.26 * var(--beam-brightness));
  filter: blur(22px) grayscale(var(--beam-gray));
}
@keyframes beam-breathe {
  0%, 100% { opacity: calc(.24 * var(--beam-brightness)); }
  50% { opacity: calc(.42 * var(--beam-brightness)); }
}

/* ---------- 3D 轮播 ---------- */
.arc-stage { position: relative; z-index: 2; height: 430px; cursor: grab; }
.arc-stage:active { cursor: grabbing; }
.arc-inner {
  position: absolute; inset: 0;
  perspective: 1400px;              /* 纵深感的来源 */
  perspective-origin: 50% 45%;
}

.arc-card {
  position: absolute; left: 50%; top: 50%;
  width: 360px;
  transform-style: preserve-3d;
  transition: transform .55s cubic-bezier(.22, .8, .28, 1), opacity .45s ease, filter .45s ease;
  background: transparent;
  border: 0;
  cursor: pointer;
}
.arc-card.active { cursor: zoom-in; }

.card-pic { width: 100%; height: 320px; display: flex; align-items: center; justify-content: center; }
.arc-img {
  width: 100%; height: 100%; object-fit: contain; display: block;
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, .40);
}
.arc-card.active .arc-img {
  box-shadow:
    0 26px 64px rgba(0, 0, 0, .5),
    0 0 0 1px color-mix(in srgb, var(--theme-primary-color) 45%, transparent),
    0 0 46px color-mix(in srgb, var(--theme-primary-color) 22%, transparent);
}
.card-halo {
  position: absolute; left: 50%; bottom: -7%;
  width: 82%; height: 26px; transform: translateX(-50%);
  background: radial-gradient(closest-side, var(--theme-primary-color), transparent 74%);
  opacity: .5; filter: blur(12px);
}

.arc-center { position: relative; z-index: 31; display: flex; justify-content: center; gap: 12px; flex-wrap: wrap; padding: 5px 12px 0; }

/* 底部：一条渐隐基光 + 指示点（选中那颗拉长并带柔光） */
.arc-dots {
  position: relative; z-index: 31;
  display: flex; justify-content: center; align-items: center;
  gap: 9px; padding: 10px 0 0;
}
.arc-dots::before {
  content: ''; position: absolute; left: 50%; bottom: 16px; transform: translateX(-50%);
  width: min(46%, 420px); height: 1px;
  background: linear-gradient(90deg, transparent,
      color-mix(in srgb, var(--theme-primary-color) 45%, transparent), transparent);
  pointer-events: none;
}
.dot {
  width: 7px; height: 7px; border-radius: 999px;
  background: color-mix(in srgb, var(--theme-primary-color) 32%, transparent);
  cursor: pointer; opacity: .85;
  transition: width .3s ease, background .3s ease, box-shadow .3s ease, opacity .3s ease;
}
.dot:hover { background: color-mix(in srgb, var(--theme-primary-color) 70%, transparent); opacity: 1; }
.dot.on {
  width: 26px; opacity: 1;
  background: linear-gradient(90deg,
      color-mix(in srgb, var(--theme-primary-color) 75%, #ffffff), var(--theme-primary-color));
  box-shadow: 0 0 12px color-mix(in srgb, var(--theme-primary-color) 65%, transparent);
}

/* 窄屏不再写死尺寸：卡片宽度、图片高度、舞台高度都由 JS 按容器量出来
   （写死在这里会被行内 style 顶掉，之前就是这么让两侧卡片跑出屏幕的） */
@media (max-width: 900px) {
  .card-halo { height: 16px; }
  /* 手指点得准一点：视觉还是小圆点，实际点击区拉到 ~25px */
  .dot { padding: 9px 0; background-clip: content-box; box-sizing: content-box; }
  .arc-dots { gap: 7px; padding-top: 8px; }
  .arc-dots::before { bottom: 22px; }
}
</style>
